import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CalendarBlank, Clock, ArrowLeft, CheckCircle, CurrencyDollar, CaretLeft, CaretRight } from "@phosphor-icons/react";
import axios from "axios";
import { getTrackingData } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_ICONS = { workshop: "shield", executive: "briefcase", private: "user" };

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselected = searchParams.get("service") || "";
  const filter = searchParams.get("filter") || "";

  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(preselected);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState("");
  const [calMonth, setCalMonth] = useState(new Date());

  useEffect(() => {
    axios.get(`${API}/services`).then(({ data }) => {
      let filtered = data;
      if (filter === "private") {
        filtered = data.filter(s => s.key.startsWith("private"));
      } else if (filter === "group") {
        filtered = data.filter(s => s.key.startsWith("group"));
      } else if (filter === "workshop") {
        filtered = data.filter(s => s.key.startsWith("workshop"));
      } else if (filter === "training") {
        filtered = data.filter(s => s.key.startsWith("group") || s.key.startsWith("workshop"));
      } else if (filter === "youth") {
        filtered = data.filter(s => s.key.startsWith("youth"));
      }
      setServices(filtered);
    });
  }, [filter]);

  useEffect(() => {
    if (selected && selectedDate) {
      axios.get(`${API}/availability/${selected}/${selectedDate}`)
        .then(({ data }) => setSlots(data.available_slots))
        .catch(() => setSlots([]));
    }
  }, [selected, selectedDate]);

  const service = services.find(s => s.key === selected);

  const handleBook = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Phone validation — at least 10 digits
    const phoneDigits = (form.phone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      setError("Please enter your phone number so we can follow up with you.");
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.post(`${API}/bookings`, {
        ...form, service_type: selected, date: selectedDate, time_slot: selectedSlot,
        tracking: getTrackingData(),
      });
      setConfirmation(data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Calendar helpers
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysInMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();
  const firstDay = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1).getDay();
  const monthLabel = calMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const isDateSelectable = (day) => {
    const d = new Date(calMonth.getFullYear(), calMonth.getMonth(), day);
    return d >= today && d.getDay() !== 0; // Not in past, not Sunday
  };

  const formatDate = (day) => {
    const d = new Date(calMonth.getFullYear(), calMonth.getMonth(), day);
    return d.toISOString().split("T")[0];
  };

  const inputClass = "bg-[#1A1A1A]/80 backdrop-blur-sm border border-white/10 rounded-md px-4 py-3 text-[#F2EFE9] placeholder:text-[#8C8C8C]/60 font-body text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none w-full transition-all";

  // Calendly for group bookings
  if (filter === "group") {
    return (
      <div className="min-h-screen bg-[#050505]">
        <header className="border-b border-white/10 px-6 py-4">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <a href="/" className="font-heading text-xl font-black tracking-tighter text-[#F2EFE9]">
              3<span className="text-[#D4AF37]">SM</span>
            </a>
            <button data-testid="booking-back" onClick={() => navigate("/")} className="text-sm text-[#8C8C8C] hover:text-[#D4AF37] transition-colors font-body flex items-center gap-1">
              <ArrowLeft size={14} /> Back to site
            </button>
          </div>
        </header>
        <CalendlyEmbed url="https://calendly.com/3sm-training/small-group-session?hide_event_type_details=1&hide_gdpr_banner=1&background_color=1a1a1a&text_color=ffffff&primary_color=fee000" />
      </div>
    );
  }

  // Confirmation
  if (step === 4 && confirmation) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <CheckCircle size={56} weight="duotone" className="text-[#D4AF37] mx-auto mb-6" />
          <h1 data-testid="booking-confirmed" className="font-heading text-3xl font-black text-[#F2EFE9] mb-2">Booking Confirmed</h1>
          <div className="glass-card border border-[#D4AF37]/30 rounded-xl p-6 mt-6 text-left space-y-3">
            <div className="flex justify-between">
              <span className="text-[#8C8C8C] font-body text-sm">Service</span>
              <span className="text-[#F2EFE9] font-body text-sm font-semibold">{confirmation.service}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#8C8C8C] font-body text-sm">Date</span>
              <span className="text-[#F2EFE9] font-body text-sm font-semibold">{new Date(confirmation.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#8C8C8C] font-body text-sm">Time</span>
              <span className="text-[#F2EFE9] font-body text-sm font-semibold">{confirmation.time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#8C8C8C] font-body text-sm">Booking ID</span>
              <span className="text-[#D4AF37] font-body text-xs">{confirmation.booking_id.slice(0, 8)}</span>
            </div>
          </div>
          <p className="text-[#8C8C8C] font-body text-sm mt-6">We'll reach out to confirm details. See you soon.</p>
          <button data-testid="booking-back-home" onClick={() => navigate("/")} className="mt-6 text-[#D4AF37] text-sm font-bold tracking-wider uppercase hover:text-[#E5C158] transition-colors">
            &larr; Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <a href="/" className="font-heading text-xl font-black tracking-tighter text-[#F2EFE9]">
            3<span className="text-[#D4AF37]">SM</span>
          </a>
          <button data-testid="booking-back" onClick={() => navigate("/")} className="text-sm text-[#8C8C8C] hover:text-[#D4AF37] transition-colors font-body flex items-center gap-1">
            <ArrowLeft size={14} /> Back to site
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {["Service", "Date & Time", "Details"].map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold font-heading ${
                step > i + 1 ? 'bg-[#D4AF37] text-[#050505]' : step === i + 1 ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]' : 'bg-[#1A1A1A] text-[#8C8C8C] border border-white/10'
              }`}>
                {step > i + 1 ? "\u2713" : i + 1}
              </div>
              <span className={`text-xs font-body hidden sm:block ${step === i + 1 ? 'text-[#F2EFE9]' : 'text-[#8C8C8C]'}`}>{label}</span>
              {i < 2 && <div className="flex-1 h-px bg-white/10" />}
            </div>
          ))}
        </div>

        {/* Step 1: Service */}
        {step === 1 && (
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#F2EFE9] mb-2">Select a Service</h1>
            <p className="text-[#8C8C8C] font-body text-sm mb-8">Choose the program that fits your goals.</p>
            <div className="space-y-3">
              {services.map(s => (
                <button
                  key={s.key}
                  data-testid={`service-option-${s.key}`}
                  onClick={() => { setSelected(s.key); setStep(2); }}
                  className={`w-full text-left glass-card border rounded-xl p-5 transition-all hover:border-[#D4AF37]/50 hover:-translate-y-0.5 ${
                    selected === s.key ? 'border-[#D4AF37]/60' : 'border-white/10'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-base font-semibold text-[#F2EFE9]">{s.name}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-[#8C8C8C] font-body flex items-center gap-1">
                          <Clock size={12} /> {s.duration} min
                        </span>
                        <span className="text-xs font-body flex items-center gap-1">
                          {s.price > 0 ? (
                            <span className="text-[#D4AF37] font-semibold">${s.price}</span>
                          ) : (
                            <span className="text-green-400">Free</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <span className="text-[#D4AF37] text-sm">&rarr;</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Date & Time */}
        {step === 2 && service && (
          <div>
            <button data-testid="back-to-step1" onClick={() => { setStep(1); setSelectedDate(""); setSelectedSlot(""); }} className="text-sm text-[#8C8C8C] hover:text-[#D4AF37] font-body mb-6 flex items-center gap-1">
              <ArrowLeft size={14} /> Change service
            </button>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#F2EFE9] mb-1">Pick a Date & Time</h1>
            <p className="text-[#D4AF37] font-body text-sm mb-8">{service.name} &middot; {service.duration} min</p>

            {/* Calendar */}
            <div className="glass-card border border-white/10 rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <button data-testid="cal-prev" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1))} className="text-[#8C8C8C] hover:text-[#D4AF37] transition-colors">
                  <CaretLeft size={20} />
                </button>
                <span className="font-heading text-sm font-semibold text-[#F2EFE9]">{monthLabel}</span>
                <button data-testid="cal-next" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1))} className="text-[#8C8C8C] hover:text-[#D4AF37] transition-colors">
                  <CaretRight size={20} />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
                  <span key={d} className="text-[10px] text-[#8C8C8C] font-body uppercase">{d}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = formatDate(day);
                  const selectable = isDateSelectable(day);
                  const isSelected = dateStr === selectedDate;
                  return (
                    <button
                      key={day}
                      data-testid={`cal-day-${day}`}
                      disabled={!selectable}
                      onClick={() => { setSelectedDate(dateStr); setSelectedSlot(""); }}
                      className={`h-9 rounded-md text-sm font-body transition-all ${
                        isSelected ? 'bg-[#D4AF37] text-[#050505] font-bold' :
                        selectable ? 'text-[#F2EFE9] hover:bg-white/10' :
                        'text-[#8C8C8C]/30 cursor-not-allowed'
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time slots */}
            {selectedDate && (
              <div>
                <p className="text-xs text-[#8C8C8C] font-body uppercase tracking-wider mb-3">
                  Available times for {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
                </p>
                {slots.length === 0 ? (
                  <p className="text-[#8C8C8C] font-body text-sm">No available slots for this date. Try another day.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {slots.map(s => (
                      <button
                        key={s}
                        data-testid={`slot-${s.replace(/\s/g, '-')}`}
                        onClick={() => setSelectedSlot(s)}
                        className={`py-2.5 rounded-md text-sm font-body transition-all border ${
                          selectedSlot === s
                            ? 'bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37] font-semibold'
                            : 'border-white/10 text-[#F2EFE9] hover:border-[#D4AF37]/40'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {selectedSlot && (
                  <button
                    data-testid="continue-to-step3"
                    onClick={() => setStep(3)}
                    className="mt-6 bg-[#D4AF37] text-[#050505] font-bold px-8 py-3 rounded-md hover:bg-[#E5C158] transition-colors uppercase tracking-widest text-sm"
                  >
                    Continue
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && service && (
          <div>
            <button data-testid="back-to-step2" onClick={() => setStep(2)} className="text-sm text-[#8C8C8C] hover:text-[#D4AF37] font-body mb-6 flex items-center gap-1">
              <ArrowLeft size={14} /> Change time
            </button>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#F2EFE9] mb-1">Your Details</h1>
            <p className="text-[#8C8C8C] font-body text-sm mb-8">Almost there. Fill in your info to confirm.</p>

            {/* Summary */}
            <div className="glass-card border border-[#D4AF37]/20 rounded-xl p-5 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#8C8C8C] font-body">Service</span>
                <span className="text-[#F2EFE9] font-body font-semibold">{service.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#8C8C8C] font-body">Date</span>
                <span className="text-[#F2EFE9] font-body">{new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#8C8C8C] font-body">Time</span>
                <span className="text-[#F2EFE9] font-body">{selectedSlot} &middot; {service.duration} min</span>
              </div>
              {service.price > 0 && (
                <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                  <span className="text-[#8C8C8C] font-body">Price</span>
                  <span className="text-[#D4AF37] font-heading font-bold">${service.price}</span>
                </div>
              )}
            </div>

            <form onSubmit={handleBook} className="space-y-4">
              <input data-testid="booking-input-name" required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="booking-input-email" required type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
                <input data-testid="booking-input-phone" required type="tel" inputMode="tel" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} />
              </div>
              <textarea data-testid="booking-input-notes" placeholder="Anything we should know? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={inputClass} />
              {error && <p data-testid="booking-error" className="text-red-400 text-sm font-body">{error}</p>}
              <button data-testid="booking-confirm-btn" type="submit" disabled={loading}
                className="w-full bg-[#D4AF37] text-[#050505] font-bold px-8 py-4 rounded-md hover:bg-[#E5C158] transition-all uppercase tracking-widest text-sm disabled:opacity-50 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]">
                {loading ? "Confirming..." : service.price > 0 ? `Confirm & Pay $${service.price}` : "Confirm Booking"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}


function CalendlyEmbed({ url }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const tryInit = () => {
      if (window.Calendly) {
        window.Calendly.initInlineWidget({
          url,
          parentElement: containerRef.current,
        });
      } else {
        setTimeout(tryInit, 500);
      }
    };
    tryInit();
  }, [url]);

  return (
    <div className="max-w-3xl mx-auto px-6 pb-10">
      <div ref={containerRef} data-testid="calendly-widget" style={{ minWidth: "320px", height: "700px" }} />
    </div>
  );
}
